import { Card, Loader, ShowAllButton, icons } from '@lifebit-ai/lds'
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { DataAccessRequestStatus } from 'modules/requests/data-access-requests/components/DataAccessRequestStatus'
import { getDataAccessRequestDetailsPath } from 'modules/requests/requestDetailPaths'
import * as metaStyles from 'modules/resultsV2/ResultMeta.styles'
import { PARTICIPANTS_IN_DATASET_LABEL } from 'modules/resultsV2/hooks/getDataAccessRequestQueryDisplayRows'
import { formatCompactParticipantCount } from 'modules/resultsV2/utils'
import type { DataAccessRequestVerificationDetail } from 'services/data-access-requests/types'
import type { RelatedDataAccessRequest } from 'services/related-data-access-requests/types'
import { useRelatedDataAccessRequests } from 'services/related-data-access-requests/useRelatedDataAccessRequests'
import { CardBody, CardHeading, CardLink } from 'ui/card/card.styles'
import { formatDate } from 'utils/date'

import * as styles from './RelatedDataAccessRequests.styles'

export const RELATED_DATA_ACCESS_REQUESTS_TEST_ID = 'related-data-access-requests'
export const RELATED_DATA_ACCESS_REQUESTS_EMPTY_TEST_ID = 'related-data-access-requests-empty'
export const RELATED_DATA_ACCESS_REQUESTS_ERROR_TEST_ID = 'related-data-access-requests-error'
export const RELATED_DATA_ACCESS_REQUESTS_UNAVAILABLE_TEST_ID = 'related-data-access-requests-unavailable'
export const RELATED_DATA_ACCESS_REQUESTS_LOADER_TEST_ID = 'related-data-access-requests-loader'
export const RELATED_DATA_ACCESS_REQUEST_SHOW_ALL_TEST_ID = 'related-data-access-request-show-all'

const EMPTY_VALUE = '-'
const EMPTY_GROUP_MESSAGE = 'This request is not part of a submission group.'
const EMPTY_LIST_MESSAGE = 'This request is not grouped with any others.'
const ERROR_MESSAGE = 'Related data access requests could not be loaded.'

export interface RelatedDataAccessRequestsProps {
  dataAccessRequest: DataAccessRequestVerificationDetail
}

interface RelatedRequestProps {
  request: RelatedDataAccessRequest
}

const MetadataRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <metaStyles.MetadataRow>
    <metaStyles.MetadataLabel>{label}</metaStyles.MetadataLabel>
    <metaStyles.MetadataValue>{children}</metaStyles.MetadataValue>
  </metaStyles.MetadataRow>
)

const RelatedRequest = ({ request }: RelatedRequestProps) => {
  const participants = request.participants

  return (
    <styles.Request>
      <MetadataRow label="Dataset">{request.datasetName}</MetadataRow>
      <MetadataRow label="Study ID">{request.studyId ?? EMPTY_VALUE}</MetadataRow>
      <MetadataRow label="Status">
        <DataAccessRequestStatus status={request.status} />
      </MetadataRow>
      <ShowAllButton data-testid={RELATED_DATA_ACCESS_REQUEST_SHOW_ALL_TEST_ID}>
        <styles.Details>
          <metaStyles.MetadataRow>
            <metaStyles.MetadataLabel>Request link</metaStyles.MetadataLabel>
            <CardLink
              as={RouterLink}
              rel="noopener noreferrer"
              target="_blank"
              to={getDataAccessRequestDetailsPath(request.dataAccessRequestId)}
            >
              <span>{request.datasetName}</span>
              <icons.OpenSmall aria-hidden />
            </CardLink>
          </metaStyles.MetadataRow>
          <MetadataRow label="Requested by">{request.requestedBy?.email ?? EMPTY_VALUE}</MetadataRow>
          <MetadataRow label="Requested on">{formatDate(request.requestedOn)}</MetadataRow>
          <MetadataRow label={PARTICIPANTS_IN_DATASET_LABEL}>
            {participants === null ? (
              EMPTY_VALUE
            ) : (
              <metaStyles.ParticipantCount>
                <icons.UserGroupSmall aria-hidden />
                <span>
                  {formatCompactParticipantCount(participants.matching)} of{' '}
                  {formatCompactParticipantCount(participants.total)}
                </span>
              </metaStyles.ParticipantCount>
            )}
          </MetadataRow>
        </styles.Details>
      </ShowAllButton>
    </styles.Request>
  )
}

export const RelatedDataAccessRequests = ({ dataAccessRequest }: RelatedDataAccessRequestsProps): JSX.Element => {
  const hasGroupId = dataAccessRequest.groupId.trim().length > 0
  const {
    relatedRequests: groupRequests,
    isLoading,
    isError,
    isSuccess
  } = useRelatedDataAccessRequests(dataAccessRequest.groupId)
  // The group endpoint returns every request in the group, including the one on screen.
  const relatedRequests = groupRequests.filter(request => request.dataAccessRequestId !== dataAccessRequest._id)

  const renderBody = () => {
    if (!hasGroupId) {
      return (
        <styles.EmptyMessage data-testid={RELATED_DATA_ACCESS_REQUESTS_UNAVAILABLE_TEST_ID}>
          {EMPTY_GROUP_MESSAGE}
        </styles.EmptyMessage>
      )
    }

    if (isLoading) {
      return (
        <div data-testid={RELATED_DATA_ACCESS_REQUESTS_LOADER_TEST_ID}>
          <Loader />
        </div>
      )
    }

    if (isError || !isSuccess) {
      return (
        <styles.EmptyMessage data-testid={RELATED_DATA_ACCESS_REQUESTS_ERROR_TEST_ID}>
          {ERROR_MESSAGE}
        </styles.EmptyMessage>
      )
    }

    if (relatedRequests.length === 0) {
      return (
        <styles.EmptyMessage data-testid={RELATED_DATA_ACCESS_REQUESTS_EMPTY_TEST_ID}>
          {EMPTY_LIST_MESSAGE}
        </styles.EmptyMessage>
      )
    }

    return (
      <styles.Requests>
        {relatedRequests.map(request => (
          <RelatedRequest key={request.dataAccessRequestId} request={request} />
        ))}
      </styles.Requests>
    )
  }

  return (
    <Card data-testid={RELATED_DATA_ACCESS_REQUESTS_TEST_ID}>
      <CardBody>
        <CardHeading>Related data access requests</CardHeading>
        {renderBody()}
      </CardBody>
    </Card>
  )
}
